import { supabase } from '../supabaseClient'

// Meal coach: data fetch + prompt builder. Mirrors workoutCoachPrompt.js
// — pure JS, no DOM, no useTranslation. Caller passes the language.

const WINDOW_DAYS = 14

export async function fetchMealContext(userId) {
  const since = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)

  const [mealsRes, profileRes] = await Promise.all([
    supabase
      .from('meal_entries')
      .select('date, meal_type, name, kcal, protein_g, carbs_g, fat_g, notes')
      .eq('user_id', userId)
      .gte('date', since)
      .order('date', { ascending: false }),
    supabase
      .from('user_profiles')
      .select('kcal_target, protein_target_g, carbs_target_g, fat_target_g')
      .eq('user_id', userId)
      .maybeSingle(),
  ])
  if (mealsRes.error) throw mealsRes.error

  return {
    meals: mealsRes.data || [],
    since,
    targets: profileRes?.data || null,
  }
}

function aggregateByDay(meals) {
  const byDay = new Map()
  for (const m of meals) {
    const d = m.date
    if (!d) continue
    const slot = byDay.get(d) || {
      date: d,
      kcal: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      mealsCount: 0,
      byType: { breakfast: 0, lunch: 0, dinner: 0, snack: 0 },
    }
    slot.kcal += Number(m.kcal) || 0
    slot.protein += Number(m.protein_g) || 0
    slot.carbs += Number(m.carbs_g) || 0
    slot.fat += Number(m.fat_g) || 0
    slot.mealsCount += 1
    if (slot.byType[m.meal_type] != null) slot.byType[m.meal_type] += 1
    byDay.set(d, slot)
  }
  return Array.from(byDay.values()).sort((a, b) => (a.date < b.date ? 1 : -1))
}

function summarise(context) {
  const days = aggregateByDay(context.meals)
  const dayCount = days.length

  const avg = (key) => {
    if (dayCount === 0) return 0
    return days.reduce((s, d) => s + d[key], 0) / dayCount
  }
  const avgKcal = avg('kcal')
  const avgProtein = avg('protein')
  const avgCarbs = avg('carbs')
  const avgFat = avg('fat')

  // Target hit rates — only meaningful if the user actually set
  // targets. Counts a day as "hit" if it lands within ±10% of target.
  const targets = context.targets || {}
  const within = (val, target) => {
    if (!target) return null
    const lo = target * 0.9
    const hi = target * 1.1
    return val >= lo && val <= hi ? 1 : 0
  }
  let kcalHits = 0; let kcalHitsBase = 0
  let proteinHits = 0; let proteinHitsBase = 0
  for (const d of days) {
    if (targets.kcal_target) {
      kcalHitsBase += 1
      kcalHits += within(d.kcal, targets.kcal_target) || 0
    }
    if (targets.protein_target_g) {
      proteinHitsBase += 1
      proteinHits += within(d.protein, targets.protein_target_g) || 0
    }
  }

  // Per-day lines (newest first). Keep it compact.
  const lines = days.slice(0, 14).map((d) => {
    const tot = `${Math.round(d.kcal)} kcal · ${Math.round(d.protein)}P · ${Math.round(d.carbs)}C · ${Math.round(d.fat)}F`
    const slots = Object.entries(d.byType)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${k}:${n}`)
      .join(' ')
    return `- ${d.date} | ${d.mealsCount} meals (${slots}) | ${tot}`
  })

  const headline = `Data window: ${dayCount} logged days (of ${WINDOW_DAYS}). Averages — kcal: ${Math.round(avgKcal)}, protein: ${Math.round(avgProtein)}g, carbs: ${Math.round(avgCarbs)}g, fat: ${Math.round(avgFat)}g.`

  const targetsLine = targets.kcal_target || targets.protein_target_g
    ? `Targets — kcal: ${targets.kcal_target ?? '—'}, protein: ${targets.protein_target_g ?? '—'}g, carbs: ${targets.carbs_target_g ?? '—'}g, fat: ${targets.fat_target_g ?? '—'}g. Days within ±10% of target — kcal: ${kcalHits}/${kcalHitsBase}, protein: ${proteinHits}/${proteinHitsBase}.`
    : 'No daily macro targets configured.'

  return {
    headline,
    targetsLine,
    perDay: lines.join('\n'),
    dayCount,
    spanDays: dayCount,
    userIsNew: dayCount < 7, // less than a week of data → baseline framing
  }
}

export function buildMealCoachPrompt(context, language = 'en') {
  const summary = summarise(context)
  const langLine = language === 'ar'
    ? 'Respond in Egyptian Arabic. Keep it warm but direct, not preachy.'
    : 'Respond in English. Direct, no fluff.'

  const framing = summary.userIsNew
    ? `The user has logged ${summary.dayCount} days of meals only — treat this as baseline-setting, not "the last week" or "the last 2 weeks".`
    : `Frame this as a check-in on the user's recent eating, scoped to the data window above.`

  return `You are a sports nutrition coach analysing one person's meal log. ${langLine}

${framing}

DATA:
${summary.headline}
${summary.targetsLine}

PER-DAY SUMMARY (newest first):
${summary.perDay || '(no meals logged)'}

YOUR TASK
Write a short coach's note (≤180 words) covering:
1. One concrete pattern in the data — e.g. protein consistently below target on rest days, kcal swing weekend-to-weekday, snacks dominating fat intake.
2. One specific gap or risk — e.g. "you skip breakfast 4 out of 7 days and that's where your protein deficit is coming from".
3. ONE concrete suggestion for the NEXT meal or day — name the meal slot, the food category, and a target macro number where it makes sense.

RULES:
- This is a one-shot output, NOT a conversation. Do NOT ask the user any questions. Do NOT invite a reply. End with the suggestion and stop.
- Use the actual numbers from the data above (kcal, P/C/F, day counts). Generic advice without numbers is not acceptable.
- If targets are not configured, ground your analysis in averages and trends instead of "hit rates".
- If the data is too thin to say something specific, say so in one sentence and still give the one concrete suggestion based on what IS there.`
}
